/**
 * Pulse `cro_variants`: Optimizely assignment on web, null on app / unknown.
 * Isolated from product code. Inspect on production:
 *   __pulseInspectCroVariants()
 */

type OptimizelyState = {
  getVariationMap?: () => unknown;
  getActiveExperimentIds?: () => unknown;
};

type OptimizelyClient = {
  get?: (key: string) => OptimizelyState | undefined;
};

const toNonEmpty = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  try {
    const json = JSON.stringify(value);
    return json && json !== '{}' && json !== '[]' ? json : null;
  } catch {
    return null;
  }
};

const pickSearch = (search: string, key: string): string | null => {
  try {
    const params = new URLSearchParams(
      search.startsWith('?') ? search : `?${search}`
    );
    const v = params.get(key);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
};

const readMeta = (name: string): string | null => {
  try {
    if (typeof document === 'undefined') return null;
    const el = document.querySelector(`meta[name="${name}"]`);
    const content = el?.getAttribute('content');
    return content && content.trim() ? content.trim() : null;
  } catch {
    return null;
  }
};

const readOptimizelyState = (): unknown => {
  try {
    const client = (window as unknown as { optimizely?: OptimizelyClient | unknown[] })
      .optimizely;
    if (!client || Array.isArray(client) || typeof client.get !== 'function') {
      return null;
    }
    const state = client.get('state');
    if (!state) return null;
    const variationMap =
      typeof state.getVariationMap === 'function'
        ? state.getVariationMap()
        : null;
    const experimentIds =
      typeof state.getActiveExperimentIds === 'function'
        ? state.getActiveExperimentIds()
        : null;
    if (variationMap && typeof variationMap === 'object') {
      return { variationMap, experimentIds };
    }
    return null;
  } catch {
    return null;
  }
};

const readWindowWf = (): unknown => {
  try {
    const wf = (window as unknown as { WF?: unknown }).WF;
    return wf && typeof wf === 'object' ? wf : null;
  } catch {
    return null;
  }
};

/**
 * Priority (first hit wins):
 * 1. URL `cro_variants` / `cro_variant` (QA)
 * 2. <meta name="cro_variants|cro_variant">
 * 3. URL `optimizely_x` (Optimizely force-variation)
 * 4. window.optimizely.get('state') — live on production
 * 5. window.WF — legacy GTM shape
 */
export const readCroVariants = (
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): string | null => {
  try {
    const fromUrl =
      pickSearch(search, 'cro_variants') || pickSearch(search, 'cro_variant');
    if (fromUrl) return fromUrl;

    const fromMeta = readMeta('cro_variants') || readMeta('cro_variant');
    if (fromMeta) return fromMeta;

    const fromOptimizelyX = pickSearch(search, 'optimizely_x');
    if (fromOptimizelyX) return fromOptimizelyX;

    const fromOptimizely = toNonEmpty(readOptimizelyState());
    if (fromOptimizely) return fromOptimizely;

    return toNonEmpty(readWindowWf());
  } catch {
    return null;
  }
};

/** Production console: `__pulseInspectCroVariants()` */
export const inspectCroVariants = (
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): Record<string, unknown> => {
  const report = {
    cro_variants: readCroVariants(search),
    url: {
      cro_variants: pickSearch(search, 'cro_variants'),
      cro_variant: pickSearch(search, 'cro_variant'),
      optimizely_x: pickSearch(search, 'optimizely_x'),
    },
    meta: {
      cro_variants: readMeta('cro_variants'),
      cro_variant: readMeta('cro_variant'),
    },
    optimizely_ready:
      typeof window !== 'undefined' &&
      !Array.isArray((window as unknown as { optimizely?: unknown }).optimizely) &&
      typeof (window as unknown as { optimizely?: OptimizelyClient }).optimizely
        ?.get === 'function',
    optimizely_state: readOptimizelyState(),
    WF: readWindowWf(),
  };
  try {
    // eslint-disable-next-line no-console
    console.info('[pulse] cro_variants inspect', report);
  } catch (_) {}
  return report;
};

export const registerCroVariantsInspector = (): void => {
  try {
    if (typeof window === 'undefined') return;
    (window as unknown as { __pulseInspectCroVariants?: typeof inspectCroVariants })
      .__pulseInspectCroVariants = inspectCroVariants;
  } catch (_) {}
};
